
import { useEffect, Component } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import RequireAuth from "./components/RequireAuth";
import DataFlowValidator from "./components/DataFlowValidator";
import SuperAdminAppShell from "./components/SuperAdminAppShell";
import AppShell from "./components/AppShell";
import { socket } from "./config/socket";
import "./styles/tailwind-globals.css";
import "./styles/responsive.css";
import { setupGlobalErrorHandling } from "./utils/errorHandling";

/* CORE PAGES */
import TripDetailView from "./pages/TripDetailView";
import TripsPage from "./pages/TripsPage";
import BookingsPage from "./pages/BookingsPage";
import QuoteWorkspace from "./modules/quotation/pages/QuoteWorkspace";

/* OTHER PAGES */
import ProfileManagement from "./pages/ProfileManagement";
import ResetPassword from "./pages/ResetPassword";
import NewDashboard from "./pages/dashboard/NewDashboard";
import HotelManagementEnhanced from "./pages/HotelManagement_Enhanced";
import TransportOperations from "./pages/operations/TransportOperations";
import TransportImport from "./pages/TransportImport";
import TransportManagementNew from "./pages/TransportManagementNew";
import CompanyManagement from "./pages/CompanyManagement";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Unauthorized from "./pages/Unauthorized";

import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import SuperAdminCompanies from "./pages/SuperAdminCompanies";
import SuperAdminUsers from "./pages/SuperAdminUsers";
import SuperAdminAdmins from "./pages/SuperAdminAdmins";
import SuperAdminAnalytics from "./pages/SuperAdminAnalytics";
import SuperAdminSettings from "./pages/SuperAdminSettings";
import SuperAdminProfile from "./pages/SuperAdminProfile";
import SuperAdminAccountSettings from "./pages/SuperAdminAccountSettings";


import AccountsManagement from "./pages/AccountsManagement";
import SalesReports from "./pages/SalesReports";
import UserManagement from "./pages/UserManagement_Enhanced";
import MasterDataManagement from "./pages/MasterDataManagement";

import TeamManagement from "./pages/TeamManagement";
import DestinationManagement from "./pages/DestinationManagement";
import HotOffersManagement from "./pages/HotOffersManagement";
import ActivityMasterManager from "./pages/ActivityMasterManager";

/* ERROR BOUNDARY */

class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("GLOBAL ERROR:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h1>Application Error</h1>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
      );
    }

    return this.props.children;
  }
}

/* AUTH WRAPPER */

const CompanyProtected = ({ children, requiredRole }) => (
  <RequireAuth requiredRole={requiredRole}>
    <AppShell>{children}</AppShell>
  </RequireAuth>
);

/* MAIN APP */

function App() {
  useEffect(() => {
    setupGlobalErrorHandling();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      socket.auth = { token };
      socket.connect();

      socket.on("connect", () => {
        console.log("✅ Socket connected");
      });

      socket.on("disconnect", () => {
        console.log("❌ Socket disconnected");
      });

      window.socket = socket;

      return () => {
        socket.disconnect();
        delete window.socket;
      };
    }
  }, []);

  return (
    <AuthProvider>
      <GlobalErrorBoundary>
        <Router>
          <DataFlowValidator>
            <Routes>

              {/* PUBLIC */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* SUPERADMIN */}
              <Route
                path="/dashboard/superadmin/*"
                element={
                  <RequireAuth requiredRole="superadmin">
                    <SuperAdminAppShell>
                      <Routes>
                        <Route index element={<SuperAdminDashboard />} />
                        <Route path="companies" element={<SuperAdminCompanies />} />
                        <Route path="users" element={<SuperAdminUsers />} />
                        <Route path="admins" element={<SuperAdminAdmins />} />
                        <Route path="analytics" element={<SuperAdminAnalytics />} />
                        <Route path="settings" element={<SuperAdminSettings />} />
                        <Route path="profile" element={<SuperAdminProfile />} />
                        <Route path="account-settings" element={<SuperAdminAccountSettings />} />
                      </Routes>
                    </SuperAdminAppShell>
                  </RequireAuth>
                }
              />

              {/* DASHBOARD */}
<Route
  path="/dashboard"
  element={
    <CompanyProtected>
        <NewDashboard />
    </CompanyProtected>
  }
/>


{/* ✅ LEADS (MAIN CRM) */}
<Route
  path="/leads"
  element={
    <CompanyProtected>
      <TripsPage />
    </CompanyProtected>
  }
/>

{/* ✅ UNIVERSAL TRIP DETAILS (USED BY BOTH LEADS & BOOKINGS) */}
<Route
  path="/trips/:id"
  element={
    <CompanyProtected>
      <TripDetailView />
    </CompanyProtected>
  }
/>
{/* ✅ BOOKINGS (SEPARATE PIPELINE PAGE) */}
<Route
  path="/bookings"
  element={
    <CompanyProtected>
      <BookingsPage />
    </CompanyProtected>
  }
/>

{/* ✅ BOOKING DETAILS */}

              {/* OPERATIONS */}
              <Route
                path="/operations"
                element={
                  <CompanyProtected>
                    <TransportOperations />
                  </CompanyProtected>
                }
              />

              <Route
                path="/transport/import"
                element={
                  <CompanyProtected>
                    <TransportImport />
                  </CompanyProtected>
                }
              />

              <Route
                path="/transport/inventory"
                element={
                  <CompanyProtected>
                    <TransportManagementNew />
                  </CompanyProtected>
                }
              />

              {/* QUOTATION */}

<Route
  path="/quotation-builder"
  element={
    <CompanyProtected>
      <QuoteWorkspace />
    </CompanyProtected>
  }
/>

<Route
  path="/quotation-builder/view/:quotationId"
  element={
    <CompanyProtected>
      <QuoteWorkspace />
    </CompanyProtected>
  }
/>

<Route
  path="/quotation-builder/edit/:quotationId"
  element={
    <CompanyProtected>
      <QuoteWorkspace />
    </CompanyProtected>
  }
/>

<Route
  path="/quotation-builder/duplicate/:quotationId"
  element={
    <CompanyProtected>
      <QuoteWorkspace />
    </CompanyProtected>
  }
/>

<Route
  path="/quotation-builder/use/:quotationId"
  element={
    <CompanyProtected>
      <QuoteWorkspace />
    </CompanyProtected>
  }
/>

<Route
  path="/company-management"
  element={
    <CompanyProtected requiredRole="admin">
      <CompanyManagement />
    </CompanyProtected>
  }
/>

              <Route
                path="/hot-offers"
                element={
                  <CompanyProtected>
                    <HotOffersManagement />
                  </CompanyProtected>
                }
              />

              {/* ADMIN */}
              <Route
                path="/users"
                element={
                  <CompanyProtected requiredRole="admin">
                    <UserManagement />
                  </CompanyProtected>
                }
              />

              <Route
                path="/teams"
                element={
                  <CompanyProtected requiredRole="admin">
                    <TeamManagement />
                  </CompanyProtected>
                }
              />

              <Route
                path="/destinations"
                element={
                  <CompanyProtected requiredRole="admin">
                    <DestinationManagement />
                  </CompanyProtected>
                }
              />

              <Route
                path="/activities"
                element={
                  <CompanyProtected requiredRole="admin">
                    <ActivityMasterManager />
                  </CompanyProtected>
                }
              />

              <Route
                path="/offers"
                element={
                  <CompanyProtected requiredRole="admin">
                    <HotOffersManagement />
                  </CompanyProtected>
                }
              />

              <Route
                path="/admin"
                element={
                  <CompanyProtected requiredRole="admin">
                    <MasterDataManagement />
                  </CompanyProtected>
                }
              />


              {/* OTHER */}
              <Route
                path="/accounts"
                element={
                  <CompanyProtected>
                    <AccountsManagement />
                  </CompanyProtected>
                }
              />

              <Route
                path="/reports"
                element={
                  <CompanyProtected>
                    <SalesReports />
                  </CompanyProtected>
                }
              />

              <Route
                path="/profile"
                element={
                  <CompanyProtected>
                    <ProfileManagement />
                  </CompanyProtected>
                }
              />

{/* HOTELS */}

<Route
  path="/hotels"
  element={
    <CompanyProtected>
      <HotelManagementEnhanced />
    </CompanyProtected>
  }
/>

<Route
  path="/hotels/new"
  element={
    <CompanyProtected requiredRole={["admin", "reservations"]}>
      <HotelManagementEnhanced />
    </CompanyProtected>
  }
/>

<Route
  path="/hotels/upload"
  element={
    <CompanyProtected requiredRole={["admin", "reservations"]}>
      <HotelManagementEnhanced />
    </CompanyProtected>
  }
/>

<Route
  path="/settings"
  element={<Navigate to="/company-management" replace />}
/>

              {/* DEFAULT */}
              <Route
  path="*"
  element={
    <div
      style={{
        padding: 40,
      }}
    >
      <h1>404</h1>
      <p>Page not found</p>
    </div>
  }
/>

            </Routes>
          </DataFlowValidator>
        </Router>
      </GlobalErrorBoundary>
    </AuthProvider>
  );
}

export default App;
